'use client';

import { useEffect, useRef, useState } from 'react';

type BackgroundOption = { id: string; label: string; stops: [string, string] };

const BACKGROUNDS: BackgroundOption[] = [
  { id: 'indigo', label: 'Indigo', stops: ['#4f46e5', '#7c3aed'] },
  { id: 'sunset', label: 'Sunset', stops: ['#f97316', '#db2777'] },
  { id: 'ocean', label: 'Ocean', stops: ['#0ea5e9', '#2563eb'] },
  { id: 'forest', label: 'Forest', stops: ['#059669', '#0d9488'] },
  { id: 'slate', label: 'Slate', stops: ['#334155', '#0f172a'] },
  { id: 'candy', label: 'Candy', stops: ['#ec4899', '#8b5cf6'] },
];

const PADDING_OPTIONS = [32, 48, 64, 96];

const DEFAULT_CODE = `function greet(name) {
  // Say hello to the world
  const message = \`Hello, \${name}!\`;
  return message;
}

console.log(greet("Cleanor"));`;

const KEYWORDS = new Set([
  'function',
  'const',
  'let',
  'var',
  'return',
  'if',
  'else',
  'for',
  'while',
  'class',
  'import',
  'export',
  'from',
  'default',
  'new',
  'async',
  'await',
  'true',
  'false',
  'null',
  'undefined',
  'def',
  'print',
  'public',
  'private',
  'static',
  'void',
]);

type Token = { text: string; color: string };

const SYNTAX = {
  base: '#e6edf3',
  keyword: '#ff7b72',
  string: '#a5d6ff',
  comment: '#8b949e',
  number: '#79c0ff',
};

// Very light, language-agnostic tokenizer for tinting. Not a real parser.
function tokenizeLine(line: string): Token[] {
  const commentIndex = (() => {
    const slash = line.indexOf('//');
    const hash = line.indexOf('#');
    const candidates = [slash, hash].filter((i) => i >= 0);
    return candidates.length ? Math.min(...candidates) : -1;
  })();

  let codePart = line;
  let commentPart = '';
  if (commentIndex >= 0) {
    codePart = line.slice(0, commentIndex);
    commentPart = line.slice(commentIndex);
  }

  const tokens: Token[] = [];
  const pattern =
    /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)|(\b\d+(?:\.\d+)?\b)|([A-Za-z_$][A-Za-z0-9_$]*)|(\s+)|([^\s\w"'`]+)/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(codePart)) !== null) {
    const [full, str, num, word, space] = match;
    if (str) tokens.push({ text: full, color: SYNTAX.string });
    else if (num) tokens.push({ text: full, color: SYNTAX.number });
    else if (word)
      tokens.push({ text: full, color: KEYWORDS.has(word) ? SYNTAX.keyword : SYNTAX.base });
    else if (space) tokens.push({ text: full, color: SYNTAX.base });
    else tokens.push({ text: full, color: SYNTAX.base });
  }
  if (commentPart) tokens.push({ text: commentPart, color: SYNTAX.comment });
  return tokens;
}

export default function BrowserCodeToImage() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [code, setCode] = useState(DEFAULT_CODE);
  const [title, setTitle] = useState('greet.js');
  const [background, setBackground] = useState('indigo');
  const [padding, setPadding] = useState(64);
  const [highlight, setHighlight] = useState(true);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = 2; // render at 2x for crisp PNG
    const fontSize = 16;
    const lineHeight = 26;
    const fontFamily = "'SFMono-Regular', 'Menlo', 'Consolas', 'Liberation Mono', monospace";
    const windowPaddingX = 24;
    const windowPaddingTop = 44; // room for chrome dots + title
    const windowPaddingBottom = 24;

    const lines = code.replace(/\t/g, '  ').split('\n');
    const measureFont = `${fontSize}px ${fontFamily}`;
    ctx.font = measureFont;

    let maxLineWidth = 0;
    for (const line of lines) {
      const w = ctx.measureText(line.length ? line : ' ').width;
      if (w > maxLineWidth) maxLineWidth = w;
    }

    const windowWidth = Math.max(320, Math.ceil(maxLineWidth) + windowPaddingX * 2);
    const windowHeight = windowPaddingTop + lines.length * lineHeight + windowPaddingBottom;
    const canvasWidth = windowWidth + padding * 2;
    const canvasHeight = windowHeight + padding * 2;

    canvas.width = canvasWidth * scale;
    canvas.height = canvasHeight * scale;
    canvas.style.width = '100%';
    canvas.style.maxWidth = `${canvasWidth}px`;
    canvas.style.height = 'auto';

    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    // Gradient background.
    const bg = BACKGROUNDS.find((b) => b.id === background) ?? BACKGROUNDS[0];
    const gradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
    gradient.addColorStop(0, bg.stops[0]);
    gradient.addColorStop(1, bg.stops[1]);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Rounded window.
    const wx = padding;
    const wy = padding;
    const radius = 12;
    ctx.beginPath();
    ctx.moveTo(wx + radius, wy);
    ctx.arcTo(wx + windowWidth, wy, wx + windowWidth, wy + windowHeight, radius);
    ctx.arcTo(wx + windowWidth, wy + windowHeight, wx, wy + windowHeight, radius);
    ctx.arcTo(wx, wy + windowHeight, wx, wy, radius);
    ctx.arcTo(wx, wy, wx + windowWidth, wy, radius);
    ctx.closePath();
    ctx.fillStyle = '#0d1117';
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 16;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Window chrome dots.
    const dotY = wy + 20;
    const dotColors = ['#ff5f56', '#ffbd2e', '#27c93f'];
    dotColors.forEach((color, index) => {
      ctx.beginPath();
      ctx.arc(wx + 20 + index * 20, dotY, 6, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    });

    // Title.
    if (title.trim()) {
      ctx.font = `13px ${fontFamily}`;
      ctx.fillStyle = '#8b949e';
      ctx.textAlign = 'center';
      ctx.fillText(title.trim(), wx + windowWidth / 2, dotY + 5);
      ctx.textAlign = 'left';
    }

    // Code lines.
    ctx.font = measureFont;
    ctx.textBaseline = 'top';
    const textX = wx + windowPaddingX;
    const textYStart = wy + windowPaddingTop;
    lines.forEach((line, index) => {
      const y = textYStart + index * lineHeight;
      if (highlight) {
        let x = textX;
        for (const token of tokenizeLine(line)) {
          ctx.fillStyle = token.color;
          ctx.fillText(token.text, x, y);
          x += ctx.measureText(token.text).width;
        }
      } else {
        ctx.fillStyle = SYNTAX.base;
        ctx.fillText(line, textX, y);
      }
    });
  }, [code, title, background, padding, highlight]);

  function handleDownload() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          setStatus('The browser could not export the image.');
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(title.trim() || 'code').replace(/[^a-z0-9._-]+/gi, '-')}.png`;
        document.body.append(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        setStatus('PNG downloaded.');
      }, 'image/png');
    } catch {
      setStatus('The browser could not export the image.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Developer</span>
        <h2 className="tool-live-title">Code to image converter</h2>
        <p className="tool-live-subtitle">
          Turn a code snippet into a shareable screenshot with window chrome and a gradient
          backdrop, then download as PNG.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Code</span>
              <textarea
                onChange={(e) => setCode(e.target.value)}
                rows={10}
                spellCheck={false}
                style={{ fontFamily: 'monospace' }}
                value={code}
              />
            </label>
            <label className="tool-live-field">
              <span>Window title (optional)</span>
              <input
                onChange={(e) => setTitle(e.target.value)}
                placeholder="example.js"
                type="text"
                value={title}
              />
            </label>
            <label className="tool-live-field">
              <span>Background</span>
              <select onChange={(e) => setBackground(e.target.value)} value={background}>
                {BACKGROUNDS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Padding</span>
              <select onChange={(e) => setPadding(Number(e.target.value))} value={padding}>
                {PADDING_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}px
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Syntax tint</span>
              <select
                onChange={(e) => setHighlight(e.target.value === 'on')}
                value={highlight ? 'on' : 'off'}
              >
                <option value="on">On</option>
                <option value="off">Off (plain)</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleDownload} type="button">
              Download PNG
            </button>
          </div>
          {status ? <p className="status-message status-success">{status}</p> : null}
        </section>

        <section className="tool-live-preview">
          <canvas ref={canvasRef} style={{ width: '100%', borderRadius: '8px' }} />
          <p className="tool-live-panel-note">
            Rendered on a canvas at 2x. Nothing is uploaded, everything runs in your browser.
          </p>
        </section>
      </div>
    </div>
  );
}
